import React, { useMemo } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/colors/teal.css";

const toPersianDigits = (value: number | string) =>
  String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const toAsciiDigits = (value: string) =>
  value.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));

export const formatJalaliPersian = (date: DateObject): string => {
  const y = toPersianDigits(date.year);
  const m = toPersianDigits(String(date.month.number).padStart(2, "0"));
  const d = toPersianDigits(String(date.day).padStart(2, "0"));
  return `${y}/${m}/${d}`;
};

const parseJalaliValue = (value: string): DateObject | undefined => {
  if (!value.trim()) return undefined;
  const parts = toAsciiDigits(value).split("/");
  if (parts.length !== 3) return undefined;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return undefined;
  return new DateObject({
    calendar: persian,
    year,
    month,
    day,
  });
};

interface PersianDatePickerProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const PersianDatePicker: React.FC<PersianDatePickerProps> = ({
  label,
  value,
  onChange,
  placeholder = "انتخاب تاریخ",
}) => {
  const pickerValue = useMemo(() => parseJalaliValue(value), [value]);

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-primary-600 hover:text-primary-700"
            >
              پاک کردن
            </button>
          )}
        </div>
      )}
      <DatePicker
        value={pickerValue ?? ""}
        calendar={persian}
        locale={persian_fa}
        calendarPosition="bottom-right"
        portal
        editable={false}
        highlightToday={false}
        onOpenPickNewDate={false}
        inputClass="input-field w-full h-[42px] min-h-[42px] box-border text-sm text-gray-900 leading-normal"
        containerClassName="w-full block"
        placeholder={placeholder}
        onChange={(date) => {
          if (!date || Array.isArray(date)) {
            onChange("");
            return;
          }
          onChange(formatJalaliPersian(date as DateObject));
        }}
      />
    </div>
  );
};

export default PersianDatePicker;
